// src/QRRedirect.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc, increment, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';

function QRRedirect() {
  const { shortId } = useParams();
  const [status, setStatus] = useState('loading');
  const [qrData, setQrData] = useState(null);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    handleQRScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortId]);

  const handleQRScan = async () => {
    try {
      const qrRef = collection(db, 'dynamic_qr_codes');
      const q = query(qrRef, where('shortId', '==', shortId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setStatus('error');
        return;
      }

      const qrDoc = snapshot.docs[0];
      const data = qrDoc.data();
      setQrData(data);

      if (!data.isActive) {
        setStatus('inactive');
        return;
      }

      if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
        setStatus('expired');
        return;
      }

      // Detect device type - NEW
      const userAgent = navigator.userAgent;
      const isMobile = /mobile|android|iphone/i.test(userAgent);
      const isTablet = /tablet|ipad/i.test(userAgent);
      const deviceType = isMobile ? 'mobile' : (isTablet ? 'tablet' : 'desktop');

      // Check if it's a new day (for daily reset) - NEW
      const lastReset = data.lastResetDate?.toDate() || new Date(0);
      const today = new Date();
      const isNewDay = lastReset.toDateString() !== today.toDateString();

      // Check if it's a new week (for weekly reset) - NEW
      const isNewWeek = getWeekNumber(lastReset) !== getWeekNumber(today);

      // Prepare update object
      const updates = {
        scanCount: increment(1),
        lastScannedAt: new Date().toISOString(),
        scans: arrayUnion({
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct',
          deviceType  // NEW
        })
      };

      // Reset daily/weekly counters if needed - NEW
      if (isNewDay) {
        updates.scansToday = 1;
        updates.lastResetDate = new Date();
      } else {
        updates.scansToday = increment(1);
      }

      if (isNewWeek) {
        updates.scansThisWeek = 1;
      } else {
        updates.scansThisWeek = increment(1);
      }

      // Update device stats - NEW
      updates[`deviceStats.${deviceType}`] = increment(1);

      await updateDoc(doc(db, 'dynamic_qr_codes', qrDoc.id), updates);

      setStatus('redirecting');

      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = data.qrContent;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    }
  };

  // Helper function to get week number - NEW
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#ffffff',
      padding: '2rem'
    }}>
      <div style={{
        background: '#fe6601',
        borderRadius: '20px',
        padding: '3rem',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        color: 'white',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
      }}>

        {status === 'loading' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid rgba(255,255,255,0.3)',
              borderTopColor: 'white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <h2>Loading...</h2>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'redirecting' && (
          <>
            <div style={{
              fontSize: '4rem',
              color: 'white',
              marginBottom: '1rem'
            }}>✓</div>
            <h2>Redirecting...</h2>
            <p style={{ color: 'white' }}>You'll be redirected in {countdown} seconds</p>

            <button
              onClick={() => window.location.href = qrData.qrContent}
              style={{
                marginTop: '1rem',
                background: 'black',
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Go Now
            </button>
          </>
        )}

        {status === 'inactive' && (
          <>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
            <h2>QR Code Inactive</h2>
            <p>This QR code has been disabled</p>
          </>
        )}

        {status === 'expired' && (
          <>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏰</div>
            <h2>QR Code Expired</h2>
            <p>This QR code is no longer valid</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
            <h2>QR Code Not Found</h2>
            <p>This QR code doesn't exist</p>
          </>
        )}

      </div>
    </div>
  );
}

export default QRRedirect;